// ============================================================================
// AREA SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { areaEndpoints, AREA_BASE_PATH } from '../endpoints/area.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Area,
  AreaListItem,
  CreateAreaRequest,
  UpdateAreaRequest,
  AreaSearchRequest,
} from '../types/area.types';

class AreaService {
  /** Paginated list — transforms backend PagedResult to frontend PaginatedResponse. */
  async getAll(params?: AreaSearchRequest): Promise<PaginatedResponse<AreaListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(AREA_BASE_PATH, params)
      : areaEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Area>> {
    const response = await get<ApiResponse<Area>>(areaEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Area>> {
    const response = await get<ApiResponse<Area>>(areaEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateAreaRequest): Promise<ApiResponse<Area>> {
    const response = await post<ApiResponse<Area>>(areaEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateAreaRequest): Promise<ApiResponse<Area>> {
    const response = await put<ApiResponse<Area>>(areaEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(areaEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${areaEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : areaEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const areaService = new AreaService();
export default areaService;
